import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import {
  ShieldCheck, Ban, LogIn, KeyRound, FileText, Clock, Users, ScrollText, ShieldAlert,
  MailWarning, MessageSquareOff, Eraser, ArrowRight, AlertTriangle, ChevronRight, Bell, FileLock2, MailCheck, ScanSearch,
} from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };

function Tile({ icon: Icon, label, value, tone = C.navy, hint, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className="text-left rounded-xl border p-3.5 transition-colors disabled:cursor-default enabled:hover:bg-[#F7FAFC]" style={{ borderColor: C.line }}>
      <div className="flex items-center gap-1.5 text-[11.5px] mb-1" style={{ color: C.slate }}>{Icon && <Icon size={13} />} {label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: tone }}>{value ?? "—"}</div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{hint}</div>}
    </button>
  );
}

const LINKS = [
  { to: "/security/audit-logs", label: "Audit logs", icon: ScrollText },
  { to: "/settings/privacy-requests", label: "Privacy requests", icon: FileLock2 },
  { to: "/security/transcript-cleanup", label: "Transcript cleanup", icon: Eraser },
  { to: "/settings/security-digest", label: "Security digest", icon: MailCheck },
  { to: "/settings/communications", label: "Communications & consent", icon: Bell },
  { to: "/settings/notification-routing", label: "Notification routing", icon: ArrowRight },
  { to: "/sops", label: "SOP library", icon: FileText },
  { to: "/documents", label: "Document Center", icon: FileLock2 },
];

export default function SecurityDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/security/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const toneMap = { red: C.red, amber: C.amber, navy: C.navy };

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="security-dashboard-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <ShieldCheck size={22} style={{ color: C.navy }} /> Security &amp; Compliance
      </h1>
      <p className="text-[13px] mb-5" style={{ color: C.slate }}>
        Access, authentication, documents, guests, privacy & consent at a glance.
        {d?.scope === "scoped" && <span style={{ color: C.amber }}> Scoped to your branches.</span>}
      </p>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
      ) : !d ? null : (
        <div className="space-y-6">
          {/* high-risk banner */}
          {d.high_risk?.length > 0 && (
            <Card className="p-3.5 border-[#EADFBF] bg-[#FBF7EC]" data-testid="security-high-risk">
              <div className="text-[12px] font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: C.amber }}><AlertTriangle size={14} /> Needs review</div>
              <div className="space-y-1">
                {d.high_risk.map((h) => (
                  <div key={h.key} data-testid="high-risk-item" className="text-[12.5px] flex items-center gap-1.5" style={{ color: toneMap[h.tone] || C.slate }}>
                    <ChevronRight size={12} /> {h.label}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            <Tile icon={Ban} label="Access denials (24h)" value={d.access_denials_24h} tone={d.access_denials_24h ? C.red : C.navy} onClick={() => navigate("/security/audit-logs?status=denied")} testid="sec-denials" />
            <Tile icon={LogIn} label="Failed logins (24h)" value={d.failed_logins_24h} tone={d.failed_logins_24h ? C.amber : C.navy} hint={`${d.login_locks_24h} lockout(s)`} testid="sec-failed-logins" />
            <Tile icon={KeyRound} label="Password resets (7d)" value={d.password_reset_requested_7d} hint={`${d.password_reset_completed_7d} completed`} testid="sec-resets" />
            <Tile icon={FileText} label="Document access (24h)" value={d.document_access_24h} testid="sec-doc-access" />
            <Tile icon={Clock} label="Guest links expiring (24h)" value={d.guests_expiring_24h} tone={d.guests_expiring_24h ? C.amber : C.navy} hint={`${d.guests_active} active`} testid="sec-guests-expiring" />
            <Tile icon={Users} label="Expired / revoked guests" value={d.guests_expired} tone={C.slate} testid="sec-guests-expired" />
            <Tile icon={ScrollText} label="Audit events (24h)" value={d.audit_events_24h} onClick={() => navigate("/security/audit-logs")} testid="sec-audit-events" />
            <Tile icon={FileLock2} label="Open privacy requests" value={d.privacy_requests_open} tone={d.privacy_requests_open ? C.amber : C.navy} onClick={() => navigate("/settings/privacy-requests")} testid="sec-privacy-open" />
            <Tile icon={Bell} label="Consent / opt-out (7d)" value={d.consent_updates_7d} testid="sec-consent" />
            <Tile icon={MessageSquareOff} label="SMS status" value={d.sms_enabled ? "ON" : "OFF"} tone={d.sms_enabled ? C.amber : C.green} hint={d.sms_enabled ? "review compliance" : "hard-gated off"} testid="sec-sms" />
            <Tile icon={ShieldAlert} label="Rate-limit blocks (24h)" value={d.rate_limit_blocks_24h} hint={`${d.spam_blocks_24h} spam blocked`} testid="sec-blocks" />
            <Tile icon={Eraser} label="Transcript cleanup pending" value={d.transcript_pending} tone={d.transcript_pending ? C.amber : C.navy} onClick={() => navigate("/security/transcript-cleanup")} testid="sec-transcript" />
          </div>

          {/* policies status */}
          <Card className="p-3.5 border-[#E6EDF2]" data-testid="security-policies">
            <div className="text-[12px] font-semibold mb-2" style={{ color: C.charcoal }}>Public policy pages</div>
            <div className="flex flex-wrap gap-2 text-[12.5px]">
              {[{ k: "privacy_policy", label: "Privacy Policy", to: "/privacy" }, { k: "terms", label: "Terms of Use", to: "/terms" }, { k: "ai_disclaimers", label: "AI disclaimers", to: null }].map((p) => (
                <span key={p.k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: d.policies?.[p.k] ? C.green : C.red }}>
                  <ShieldCheck size={12} /> {p.label}: {d.policies?.[p.k] ? "Present" : "Missing"}
                  {p.to && <a href={p.to} target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: C.navy }}>view</a>}
                </span>
              ))}
            </div>
          </Card>

          {/* recent denials */}
          <Card className="p-3.5 border-[#E6EDF2]" data-testid="security-recent-denials">
            <div className="text-[12px] font-semibold mb-2 flex items-center gap-1.5" style={{ color: C.charcoal }}><Ban size={14} style={{ color: C.red }} /> Recent access denials</div>
            {(d.recent_denials || []).length === 0 ? (
              <div className="text-[12.5px]" style={{ color: C.slate }}>No denials in the last 7 days.</div>
            ) : (
              <div className="space-y-1">
                {d.recent_denials.map((r, i) => (
                  <div key={i} data-testid="denial-row" className="text-[12px] flex items-center justify-between gap-3 border-b last:border-0 py-1" style={{ borderColor: C.line }}>
                    <span style={{ color: C.charcoal }}>{String(r.action).replace(/_/g, " ")} <span style={{ color: C.slate }}>· {r.user_email || "system"} {r.role ? `(${String(r.role).replace(/_/g, " ")})` : ""}</span></span>
                    <span style={{ color: C.slate }}>{r.resource_type || ""} · {formatDateTime(r.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* quick links */}
          <div>
            <div className="text-[12px] font-semibold mb-2" style={{ color: C.charcoal }}>Manage</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {LINKS.map((l) => (
                <button key={l.to} onClick={() => navigate(l.to)} data-testid={`sec-link-${l.to.replace(/\//g, "-")}`}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] hover:bg-[#F7FAFC] transition-colors" style={{ borderColor: C.line, color: C.navy }}>
                  <l.icon size={15} /> {l.label}
                </button>
              ))}
            </div>
          </div>

          {user?.role === "super_admin" && (
            <div>
              <div className="text-[12px] font-semibold mb-2 flex items-center gap-1.5" style={{ color: C.charcoal }}>
                <ShieldAlert size={13} style={{ color: C.red }} /> Launch prep (Super Admin)
              </div>
              <button onClick={() => navigate("/security/demo-data-cleanup")} data-testid="sec-link-demo-cleanup"
                className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] hover:bg-[#FBF7EC] transition-colors" style={{ borderColor: "#EADFBF", color: C.amber }}>
                <ScanSearch size={15} /> Demo / test data cleanup
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
